import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AnalyticReport,
  AnalyticReportDataRow,
  FieldDefinition
} from '../../types/analytic-reports/AnalyticReport';

type TemporalReportProps = {
  report: AnalyticReport;
  onDateRangeChange?: (startDate: string, endDate: string) => Promise<void> | void;
  loading?: boolean;
};

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const TRENDLINE_COLOR = '#c100ff';

const formatValue = (value: unknown, fieldType: FieldDefinition['fieldType']): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (fieldType === 'number' || fieldType === 'currency' || fieldType === 'percentage') {
    return numberFormatter.format(Number(value));
  }
  return String(value);
};

const toNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getTemporalFieldDefinitions = (report: AnalyticReport): FieldDefinition[] => {
  if (report.fieldDefinitions?.length) return report.fieldDefinitions;
  return [
    { name: 'date', label: 'Date', fieldType: 'date' },
    { name: 'value', label: 'Value', fieldType: 'number' }
  ];
};

const stripMarkdownHeading = (value?: string) => (value || '').replace(/^#+\s*/, '').trim();

const toCsv = (rows: AnalyticReportDataRow[], fieldDefinitions: FieldDefinition[]) => {
  const headers = fieldDefinitions.map((field) => field.label);
  const headerKeys = fieldDefinitions.map((field) => field.name);
  const lines = rows.map((row) => headerKeys.map((key) => `${row[key] ?? ''}`));
  const allRows = [headers, ...lines];
  return allRows
    .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};

/** Cubic Bézier smooth path through points (Catmull–Rom-style control points, tension ~1/6). */
const smoothLinePath = (
  points: Array<{ x: number; y: number }>,
  minY?: number,
  maxY?: number
): string => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1yRaw = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2yRaw = p2.y - (p3.y - p1.y) / 6;
    const cp1y = minY !== undefined && maxY !== undefined
      ? Math.min(maxY, Math.max(minY, cp1yRaw))
      : cp1yRaw;
    const cp2y = minY !== undefined && maxY !== undefined
      ? Math.min(maxY, Math.max(minY, cp2yRaw))
      : cp2yRaw;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

type TemporalLineChartProps = {
  rows: AnalyticReportDataRow[];
  xKey: string;
  yKey: string;
  xFieldType: FieldDefinition['fieldType'];
  yFieldType: FieldDefinition['fieldType'];
  /** Report `units` (e.g. ballots) — shown after the daily total in the tooltip */
  units?: string;
  trendlineEnabled?: boolean;
};

const TemporalLineChart: React.FC<TemporalLineChartProps> = ({
  rows,
  xKey,
  yKey,
  xFieldType,
  yFieldType,
  units,
  trendlineEnabled = false,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gradientId = useId().replace(/:/g, '');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null);

  const layout = useMemo(() => {
    const width = 980;
    const height = 300;
    const padding = 36;

    if (!rows.length) {
      return {
        linePath: '',
        areaPath: '',
        trendlinePath: '',
        labels: [] as Array<{ x: number; label: string }>,
        yTicks: [] as Array<{ y: number; label: string }>,
        chartPoints: [] as Array<{ x: number; y: number }>,
        padding,
        width,
        height
      };
    }

    const maxY = Math.max(...rows.map((row) => toNumber(row[yKey])), 0);
    const safeMaxY = maxY <= 0 ? 1 : maxY;
    const stepX = rows.length > 1 ? (width - (padding * 2)) / (rows.length - 1) : 0;

    const chartPoints = rows.map((row, index) => {
      const x = padding + (stepX * index);
      const y = height - padding - ((toNumber(row[yKey]) / safeMaxY) * (height - (padding * 2)));
      return { x, y };
    });

    const bottomY = height - padding;
    const linePath = smoothLinePath(chartPoints, padding, bottomY);
    const last = chartPoints[chartPoints.length - 1];
    const first = chartPoints[0];
    const areaPath = `${linePath} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
    let trendlinePath = '';

    if (trendlineEnabled && rows.length > 1) {
      const points = rows.map((row, index) => ({ x: index, y: toNumber(row[yKey]) }));
      const n = points.length;
      const sumX = points.reduce((acc, point) => acc + point.x, 0);
      const sumY = points.reduce((acc, point) => acc + point.y, 0);
      const sumXY = points.reduce((acc, point) => acc + (point.x * point.y), 0);
      const sumXX = points.reduce((acc, point) => acc + (point.x * point.x), 0);
      const denominator = (n * sumXX) - (sumX * sumX);
      const slope = denominator === 0 ? 0 : ((n * sumXY) - (sumX * sumY)) / denominator;
      const intercept = (sumY - (slope * sumX)) / n;
      const clampValue = (value: number) => Math.min(safeMaxY, Math.max(0, value));
      const toChartY = (value: number) => (
        height - padding - ((value / safeMaxY) * (height - (padding * 2)))
      );

      const trendStart = clampValue(intercept);
      const trendEnd = clampValue((slope * (n - 1)) + intercept);
      trendlinePath = `M ${first.x} ${toChartY(trendStart)} L ${last.x} ${toChartY(trendEnd)}`;
    }

    const labels = rows
      .filter((_, index) => index === 0 || index === rows.length - 1 || index % Math.max(1, Math.ceil(rows.length / 8)) === 0)
      .map((row, index, arr) => {
        const actualIndex = rows.findIndex((candidate) => candidate === row);
        const x = padding + (stepX * actualIndex);
        const label = String(row[xKey] ?? '');
        return { x, label: index === arr.length - 1 && label.length > 10 ? label.slice(0, 10) : label.slice(5) };
      });

    const tickCount = 4;
    const yTicks = [...Array(tickCount + 1).keys()].map((tick) => {
      const ratio = tick / tickCount;
      const y = height - padding - (ratio * (height - (padding * 2)));
      return {
        y,
        label: numberFormatter.format(Math.round(safeMaxY * ratio))
      };
    });

    return { linePath, areaPath, trendlinePath, labels, yTicks, chartPoints, padding, width, height };
  }, [rows, xKey, yKey, trendlineEnabled]);

  const nearestIndexFromSvgX = useCallback(
    (svgX: number) => {
      if (!layout.chartPoints.length) return 0;
      let best = 0;
      let bestDist = Infinity;
      layout.chartPoints.forEach((point, index) => {
        const dist = Math.abs(svgX - point.x);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      return best;
    },
    [layout.chartPoints]
  );

  const handleSvgMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container || !layout.chartPoints.length) return;

    const rect = svg.getBoundingClientRect();
    const svgX = ((event.clientX - rect.left) / rect.width) * layout.width;
    const index = nearestIndexFromSvgX(svgX);
    setHoverIndex(index);

    const containerRect = container.getBoundingClientRect();
    setTooltipPos({
      left: event.clientX - containerRect.left + 12,
      top: event.clientY - containerRect.top - 8,
    });
  };

  const handleSvgMouseLeave = () => {
    setHoverIndex(null);
    setTooltipPos(null);
  };

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No temporal data available.
      </Typography>
    );
  }

  const hoveredRow = hoverIndex !== null ? rows[hoverIndex] : null;
  const hoveredPoint = hoverIndex !== null ? layout.chartPoints[hoverIndex] : null;

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        width="100%"
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        preserveAspectRatio="none"
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={handleSvgMouseLeave}
        style={{ cursor: 'crosshair', display: 'block' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8ea2ea" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#8ea2ea" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {layout.yTicks.map((tick) => (
          <g key={`grid-${tick.y}`}>
            <line x1="36" y1={tick.y} x2={layout.width - 36} y2={tick.y} stroke="#ececf4" strokeWidth="1" />
            <text x="10" y={tick.y + 4} fontSize="10" fill="#8a8ea3">
              {tick.label}
            </text>
          </g>
        ))}
        <path d={layout.areaPath} fill={`url(#${gradientId})`} />
        {layout.trendlinePath && (
          <path
            d={layout.trendlinePath}
            fill="none"
            stroke={TRENDLINE_COLOR}
            strokeWidth="2"
            strokeDasharray="7 5"
            strokeLinecap="round"
          />
        )}
        <path d={layout.linePath} fill="none" stroke="#95a2d9" strokeWidth="3" strokeLinecap="round" />
        {hoveredPoint && (
          <g pointerEvents="none">
            <line
              x1={hoveredPoint.x}
              y1={layout.padding}
              x2={hoveredPoint.x}
              y2={layout.height - layout.padding}
              stroke="#95a2d9"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity={0.9}
            />
            <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={5} fill="#fff" stroke="#95a2d9" strokeWidth={2} />
          </g>
        )}
        {layout.labels.map((label) => (
          <text key={`${label.x}-${label.label}`} x={label.x} y={layout.height - 12} textAnchor="middle" fontSize="10" fill="#8a8ea3">
            {label.label}
          </text>
        ))}
      </svg>
      {hoveredRow && tooltipPos && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            left: tooltipPos.left,
            top: tooltipPos.top,
            transform: 'translateY(-100%)',
            pointerEvents: 'none',
            zIndex: 2,
            px: 1.25,
            py: 0.75,
            minWidth: 120,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" display="block" color="text.secondary">
            {formatValue(hoveredRow[xKey], xFieldType)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatValue(hoveredRow[yKey], yFieldType)}
            {units ? ` ${units}` : ''}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

const TemporalReport: React.FC<TemporalReportProps> = ({
  report,
  onDateRangeChange,
  loading = false
}) => {
  const fieldDefinitions = getTemporalFieldDefinitions(report);
  const rows = (report.result?.data || []) as AnalyticReportDataRow[];
  const total = report.result?.total;
  const [startDateInput, setStartDateInput] = useState<string>('');
  const [endDateInput, setEndDateInput] = useState<string>('');

  const xFieldDef = fieldDefinitions.find((field) => field.fieldType === 'date')
    || fieldDefinitions[0]
    || { name: 'date', label: 'Date', fieldType: 'date' as const };
  const yFieldDef = fieldDefinitions.find((field) => (
    field.fieldType === 'number' || field.fieldType === 'currency' || field.fieldType === 'percentage'
  ))
    || fieldDefinitions[1]
    || { name: 'value', label: 'Value', fieldType: 'number' as const };
  const xField = xFieldDef.name;
  const yField = yFieldDef.name;
  const temporalTitle = stripMarkdownHeading(report.title) || 'Users per Day';
  const currentStartDate = String(report.result?.queryParams?.startDate || report.startDate || '');
  const currentEndDate = String(report.result?.queryParams?.endDate || report.endDate || '');

  useEffect(() => {
    setStartDateInput(currentStartDate);
    setEndDateInput(currentEndDate);
  }, [currentStartDate, currentEndDate]);

  const handleExportCsv = () => {
    const csvContent = toCsv(rows, fieldDefinitions);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', `${report.key || 'temporal-report'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleApplyDateRange = async () => {
    if (!onDateRangeChange) return;
    await onDateRangeChange(startDateInput, endDateInput);
  };

  const canRunDateRangeChange = Boolean(
    onDateRangeChange
    && startDateInput
    && endDateInput
    && (startDateInput !== currentStartDate || endDateInput !== currentEndDate)
  );

  const handleDateFieldBlur = async () => {
    if (!canRunDateRangeChange || loading) return;
    await handleApplyDateRange();
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', width: '100%' }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
          <TextField
            size="small"
            type="date"
            label="Start Date"
            value={startDateInput}
            onChange={(event) => setStartDateInput(event.target.value)}
            onBlur={handleDateFieldBlur}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            type="date"
            label="End Date"
            value={endDateInput}
            onChange={(event) => setEndDateInput(event.target.value)}
            onBlur={handleDateFieldBlur}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
        </Stack>
        <Tooltip title="Download as tabular data" placement="top">
          <Box component="span" sx={{ ml: 'auto', lineHeight: 0 }}>
            <IconButton
              type="button"
              size="small"
              onClick={handleExportCsv}
              disabled={rows.length === 0}
              aria-label="Download as tabular data"
            >
              <TableChartOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>

      <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {temporalTitle}
        </Typography>
        {typeof total === 'number' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total: {numberFormatter.format(total)} {report.units || ''}
          </Typography>
        )}
        <TemporalLineChart
          rows={rows}
          xKey={xField}
          yKey={yField}
          xFieldType={xFieldDef.fieldType}
          yFieldType={yFieldDef.fieldType}
          units={report.units}
          trendlineEnabled={Boolean(report.trendlineEnabled)}
        />
      </Paper>
    </Stack>
  );
};

export default TemporalReport;
